import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  // Removed Autocomplete, Select, MenuItem, FormControlLabel, Checkbox, ListItem, ListItemText, Tooltip
} from "@mui/material";
// Assuming these are available in your environment
import { Colors } from "../../../../Styles/Colors";
import ShowComponents from "../Common/ShowComponent";
import { renderIconButton, renderTitle } from "../Common/DocCommonComponent";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import {
  CommonDeleteButton,
  CommonEditButton,
  DeletePopup,
} from "../../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../../Utility/ImagePaths";
import RestAPIService from "../../../../Utility/Services/RestAPIService";

// Define headers based on the image: From, Brand, Molecule, Strength, Dosage, Action
const headers = ["From", "Brand", "Molecule", "Strength", "Dosage", "Action"];

// Placeholder for the Allopathic Medication API endpoint.
const ALLOPATHIC_MEDICATION_URL = Serviceurls.AYURVEDA_ALLOPATHIC_MEDICATION;

const DocCurrentAllopathicMedication = ({ Title, ...props }) => {
  const [state, setState] = useState({
    // Input row state (all simple text fields now)
    from: "",
    brand: "",
    molecule: "",
    strength: "",
    dosage: "",

    // Fetched data
    medicationData: [],

    // UI state
    isEdit: false,
    selectedId: null,
    postLoading: false,
    dltLineClk: false,
    deleteLineItem: null,
    removeLineData: false,
  });

  const updateState = (updates) => setState((prev) => ({ ...prev, ...updates }));

  // --- READ (GET) Functionality ---
  const getMedicationData = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
      const res = await RestAPIService.getAll(
        `${ALLOPATHIC_MEDICATION_URL}${params}`
      );
      if (res?.data?.status === "success") {
        updateState({ medicationData: res.data.data || [] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message || "Failed to load medication data");
    }
  };

  // getApiCall is the main function lazy-loaded by ShowComponents
  const getApiCall = useCallback(() => {
    getMedicationData();
  }, [props.appointmentId, props.isFromIp, props.ipAdmissionId]);

  useEffect(() => {
    props.registerRefreshFunction("current_allopathic_medication", getApiCall);
  }, []);

  // Clear Handler
  const onClearHandler = useCallback(() => {
    updateState({
      isEdit: false,
      selectedId: null,
      from: "",
      brand: "",
      molecule: "",
      strength: "",
      dosage: "",
    });
  }, []);

  // --- CREATE/UPDATE (POST/PUT) Functionality ---
  const postMedication = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const data = {
        id: state.selectedId ? state.selectedId : null,
        from_field: state.from || "",
        brand: state.brand || "",
        molecule: state.molecule || "",
        strength: state.strength || "",
        dosage: state.dosage || "",
      };
      if (isFromIp) {
        data.ip_admission_id = ipAdmissionId;
        data.clinic_id = clinicId;
        data.doctor_id = doctorId;
      } else {
        data.appointment_id = appointmentId;
      }

      updateState({ postLoading: true });

      const apiCall = state.selectedId
        ? RestAPIService.update(state.selectedId, data, ALLOPATHIC_MEDICATION_URL)
        : RestAPIService.create(data, ALLOPATHIC_MEDICATION_URL);

      const res = await apiCall;
      if (res?.data?.status === "success") {
        props.successMessage(res.data.message || (state.selectedId ? "Updated successfully" : "Added successfully"));
        onClearHandler();
        getMedicationData();
      } else {
        props.errorMessage(res?.data?.message || "Save failed");
      }
      updateState({ postLoading: false });
    } catch (e) {
      updateState({ postLoading: false });
      props.errorMessage(e?.response?.data?.message || e.message || "An error occurred during save.");
    }
  };

  // --- EDIT Functionality (populates top row) ---
  const onEdit = useCallback((item) => {
    updateState({
      isEdit: true,
      selectedId: item.id,
      from: item.from_field || "",
      brand: item.brand || "",
      molecule: item.molecule || "",
      strength: item.strength || "",
      dosage: item.dosage || "",
    });
  }, []);

  // --- DELETE Functionality ---
  const deleteLinePop = () => {
    updateState({
      dltLineClk: false,
      deleteLineItem: null,
      removeLineData: false,
    });
  };

  const deleteLineItem = async () => {
    try {
      updateState({ removeLineData: true });
      // Delete endpoint assumed to be /endpoint/{id}/
      await RestAPIService.delete(`${ALLOPATHIC_MEDICATION_URL}${state.deleteLineItem?.id}/`);
      props.successMessage("Deleted successfully");
      deleteLinePop();
      getMedicationData(); // Refresh list
    } catch (e) {
      updateState({ removeLineData: false });
      props.errorMessage(e?.response?.data?.message || e.message || "Failed to delete.");
    }
  };

  // --- Validation and UI State ---
  const disableBtn = useMemo(() => {
    const { from, brand, molecule, strength, dosage } = state;
    // Button is disabled if no fields are filled OR if the component is locked
    return (
      !(from && brand && molecule && strength && dosage) ||
      props.isLocked ||
      state.postLoading
    );
  }, [
    state.from,
    state.brand,
    state.molecule,
    state.strength,
    state.dosage,
    state.postLoading,
    props.isLocked,
  ]);

  // --- Render Helpers (Text Field) ---
  const renderTextField = (label, stateKey, maxLength = 250, width = "100%") => (
    <TextField
      size="small"
      autoComplete="off"
      label={label}
      value={state[stateKey] || ""}
      inputProps={{ maxLength }}
      onChange={(e) => updateState({ [stateKey]: e.target.value })}
      sx={{ width: width }}
      disabled={props.isLocked && !state.isEdit} // Disable when locked unless actively editing
    />
  );

  return (
    <ShowComponents onIntersection={getApiCall}>
      <Card
        sx={{
          width: "100%",
          height: "fit-content",
          padding: "0.3vw",
          marginBottom: "0.5vw",
          border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`,
        }}
      >
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"space-between"} padding={"0.5vw"} borderBottom={`1px solid #E3E2E2`}>
          {renderTitle("Current Allopathic Medication", props.isSelected)}
        </Box>

        <Table size="small" stickyHeader aria-label="medication table" sx={{ maxHeight: "28vw", overflowY: "auto" }}>
          <TableHead>
            <TableRow>
              {headers.map((h, idx) => (
                <TableCell
                  key={idx}
                  align={idx === headers.length - 1 ? "center" : "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9vw",
                    color: Colors.SecondaryText,
                    backgroundColor: Colors.Background,
                    px: "0.2vw",
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* INPUT ROW */}
            <TableRow sx={{ "& td": { px: "0.2vw", verticalAlign: "middle" } }}>
              <TableCell sx={{ width: "18vw" }}>{renderTextField("From *", "from", 100)}</TableCell>
              <TableCell sx={{ width: "18vw" }}>{renderTextField("Brand *", "brand", 100)}</TableCell>
              <TableCell sx={{ width: "18vw" }}>{renderTextField("Molecule *", "molecule", 100)}</TableCell>
              <TableCell sx={{ width: "10vw" }}>{renderTextField("Strength *", "strength", 50)}</TableCell>
              <TableCell sx={{ width: "18vw" }}>{renderTextField("Dosage *", "dosage", 100)}</TableCell>

              <TableCell align="center" sx={{ width: "1vw", display: "flex" }}>
                {renderIconButton(
                  state.isEdit ? "Update" : "Add",
                  state.isEdit ? ImagePaths.TickEnable.default : disableBtn ? ImagePaths.DisabledAddIcon.default : ImagePaths.AddIcon.default,
                  postMedication,
                  disableBtn,
                  "1.8vw"
                )}
                {state.isEdit && renderIconButton( // Add a clear/cancel button when editing
                  "Cancel",
                  ImagePaths.ClearIcon.default,
                  onClearHandler,
                  false,
                  "1.8vw",
                  true // isCancel prop to use a different color/style
                )}
              </TableCell>
            </TableRow>

            {/* EXISTING ROWS */}
            {state.medicationData?.length > 0 ? (
              state.medicationData.map((item, idx) => (
                <TableRow key={item.id || idx} sx={{ "& td": { pl: "0.4vw", pr: "0.7vw", fontSize: "0.9vw" } }}>
                  <TableCell>{item.from_field || "-"}</TableCell>
                  <TableCell>{item.brand || "-"}</TableCell>
                  <TableCell>{item.molecule || "-"}</TableCell>
                  <TableCell>{item.strength || "-"}</TableCell>
                  <TableCell>{item.dosage || "-"}</TableCell>

                  <TableCell align="center">
                    <Box component={"div"} display={"flex"} justifyContent={'center'}>
                      <CommonEditButton
                        disable={state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => onEdit(item)}
                      />
                      <CommonDeleteButton
                        disable={state.isEdit || state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => updateState({ dltLineClk: true, deleteLineItem: item })}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No Records To Be Shown
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Delete Confirmation Popup */}
        {state.dltLineClk && (
          <DeletePopup
            DeletTitle={`Are you sure you want to delete this medication?`}
            deleteAlertPopupClose={deleteLinePop}
            removeData={deleteLineItem}
            disable={state.removeLineData}
          />
        )}
      </Card>
    </ShowComponents>
  );
};

export default DocCurrentAllopathicMedication;